import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Instagram, Phone, Mail, MapPin, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { LOGO_URL, COMPANY_ADDRESS } from "../mock";
import { submitLead } from "../lib/api";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ status: "loading", message: "" });
    try {
      await submitLead({ email, source: "footer", formType: "newsletter" });
      setState({ status: "success", message: "Subscribed! Watch your inbox." });
      setEmail("");
      setTimeout(() => setState({ status: "idle", message: "" }), 5000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setState({
        status: "error",
        message: typeof detail === "string" ? detail : "Please enter a valid email.",
      });
    }
  };

  return (
    <footer className="bg-[#0A1F5C] text-gray-300 pt-12 pb-6 mt-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 pb-8 border-b border-white/10">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-md p-2 inline-block mb-3">
              {/* LOGO_URL's source jpg has heavy built-in whitespace around the mark; crop it here instead of shrinking the badge. */}
              <div className="relative overflow-hidden" style={{ width: "82px", height: "64px" }}>
                <img
                  src={LOGO_URL}
                  alt="SkillsGain"
                  style={{ position: "absolute", width: "127px", height: "127px", maxWidth: "none", left: "-22.7px", top: "-27px" }}
                />
              </div>
            </div>
            <p className="leading-relaxed text-gray-300 max-w-sm text-sm">
              SkillsGain is a global learning &amp; certification partner empowering enterprises with future-ready AI, Cloud, Data and Cybersecurity skills.
            </p>
            <div className="flex gap-3 mt-4">
              {[
                { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/skills-gain/?viewAsMember=true" },
                { Icon: Twitter, label: "Twitter", href: "#" },
                { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61591457900452" },
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/skillsgain/" },
              ].map(({ Icon, label, href }, i) => (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#6BBE32] transition-colors">
                  <Icon size={16}/>
                </a>
              ))}
            </div>
          </div>

          <FooterCol title="Resources" links={[
            { label: "Blogs", to: "/resources/blog" },
            { label: "Webinars", to: "/resources/webinars" }
          ]}/>
          <FooterCol title="Company" links={[
            { label: "Contact", to: "/contact" }
          ]}/>

          <div>
            <h4 className="text-white font-semibold mb-3">Get In Touch</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <MapPin size={14} className="text-[#8BD835] shrink-0 mt-0.5"/>
                <span className="leading-snug">
                  {COMPANY_ADDRESS.line1}<br/>
                  {COMPANY_ADDRESS.line2}<br/>
                  {COMPANY_ADDRESS.line3}<br/>
                  {COMPANY_ADDRESS.line4}
                </span>
              </li>
              <li className="flex gap-2"><Phone size={14} className="text-[#8BD835] shrink-0 mt-0.5"/> +91 80 4567 8900</li>
              <li className="flex gap-2"><Mail size={14} className="text-[#8BD835] shrink-0 mt-0.5"/> info@skillsgain.com</li>
            </ul>
            <form className="mt-4 flex bg-white/10 rounded-md overflow-hidden border border-white/10" onSubmit={handleSubscribe} data-testid="newsletter-form">
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Subscribe to newsletter"
                data-testid="newsletter-email"
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder-gray-400 outline-none"
              />
              <button type="submit" disabled={state.status === "loading"} data-testid="newsletter-submit"
                className="bg-[#6BBE32] hover:bg-[#4F9622] shrink-0 transition-colors disabled:opacity-60"
                style={{
                  width: 44,
                  alignSelf: "stretch",
                  padding: 0,
                  margin: 0,
                  border: 0,
                  boxSizing: "border-box",
                  display: "grid",
                  placeItems: "center",
                  lineHeight: 0
                }}
                aria-label="Subscribe">
                {state.status === "loading"
                  ? <Loader2 size={16} className="text-white animate-spin" style={{ display: "block", margin: "auto" }}/>
                  : <ArrowRight size={16} className="text-white" style={{ display: "block", margin: "auto" }}/>}
              </button>
            </form>
            {state.status === "success" && (
              <p data-testid="newsletter-success" className="mt-2 text-xs text-[#8BD835] flex items-center gap-1">
                <CheckCircle2 size={12}/> {state.message}
              </p>
            )}
            {state.status === "error" && (
              <p data-testid="newsletter-error" className="mt-2 text-xs text-red-300">{state.message}</p>
            )}
          </div>
        </div>

        <div className="pt-6 text-center text-xs text-gray-400">
          <p>© {new Date().getFullYear()} SkillsGain Pvt. Ltd. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="text-white font-semibold mb-4">{title}</h4>
    <ul className="space-y-2 text-sm">
      {links.map(l => (
        <li key={l.label}><Link to={l.to} className="hover:text-[#8BD835] transition-colors">{l.label}</Link></li>
      ))}
    </ul>
  </div>
);

export default Footer;
